import React, { useMemo, useRef, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard, Factory, TrendingUp, Gem, DollarSign, Banknote, TrainFront,
  Scale, Pickaxe, Building2, FileText, Upload, Archive, FileDown, Settings,
  LogOut, ChevronLeft, ChevronRight, Menu, Globe2, MapPin, Bookmark, Bell,
  HelpCircle, Map, Sparkles, Crown, Workflow, Newspaper, Zap, Mountain, Search,
  Activity,
  type LucideIcon,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { categories } from '../../config/categories.config';
import { useAuth } from '../../auth/useAuth';
import { useUIStore } from '../../store';
import { useFilterStore } from '../../hooks/useFilters';
import { useDashboardSummary } from '../../hooks/useNews';
import { getCurrentUser } from '../../services/users.api';

/** One navigable row in the sidebar. */
interface NavRow {
  key: string;
  to: string;
  label: string;
  icon: LucideIcon;
  /** Category slug used for the live tally and for permittedTabs gating. */
  countSlug?: string;
  /** REC child routes revealed while this row is the active one. */
  recs?: { code: string; label: string; intel?: boolean }[];
}

interface NavGroup {
  key: string;
  label: string;
  rows: NavRow[];
}

const categoryIcons: Record<string, LucideIcon> = {
  LayoutDashboard, Factory, TrendingUp, Gem, DollarSign, Banknote, TrainFront,
  Scale, Pickaxe, Building2, FileText, Upload,
};

/**
 * Left navigation — Brand Standards v2.
 *
 * 240px navy rail: logo, search, grouped nav (Intelligence / Coverage /
 * Regions / Analysis / Workspace), user card. The active row takes the gold
 * treatment; counts are today's per-category tallies, hidden at zero.
 */
export const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const {
    isSidebarOpen, isSidebarCollapsed, setSidebarOpen, toggleSidebarCollapse,
  } = useUIStore();
  const { searchQuery, setSearch } = useFilterStore();
  const searchRef = useRef<HTMLInputElement>(null);

  const isAdmin = user?.role === 'admin';
  const isStaff = user?.role === 'admin' || user?.role === 'internal';

  const { data: profile } = useQuery({
    queryKey: ['userProfile'],
    queryFn: getCurrentUser,
    staleTime: 10 * 60 * 1000,
    enabled: !!user,
  });
  const permittedTabs = (profile as any)?.permittedTabs as string[] | null | undefined;
  const subscriptionTier = (profile as any)?.subscriptionTier as string | undefined;
  const canSeeBriefs = isStaff || subscriptionTier === 'enterprise';

  // Live per-category tallies for the last 24h. Shares the dashboard summary
  // query, so the sidebar costs no extra request.
  const { data: summary } = useDashboardSummary();
  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    (summary?.categoryCountsToday ?? []).forEach((c) => { map[c.slug] = c.count; });
    return map;
  }, [summary]);

  // Cmd-K / Ctrl-K focuses the intelligence search.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSidebarOpen(true);
        searchRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setSidebarOpen]);

  /** Category tabs the current user is allowed to see. */
  const allowed = useMemo(() => {
    if (isStaff) return new Set(categories.map((c) => c.slug));
    if (!permittedTabs || permittedTabs.length === 0) {
      return new Set(categories.map((c) => c.slug));
    }
    return new Set(['dashboard', ...permittedTabs]);
  }, [isStaff, permittedTabs]);

  const groups: NavGroup[] = useMemo(() => {
    const catRow = (slug: string): NavRow | null => {
      const cat = categories.find((c) => c.slug === slug);
      if (!cat || !allowed.has(slug)) return null;
      return {
        key: slug,
        to: slug === 'dashboard' ? '/dashboard' : `/${slug}`,
        label: t(`categories.${slug}`),
        icon: categoryIcons[cat.icon] ?? LayoutDashboard,
        countSlug: slug,
      };
    };
    const compact = (rows: (NavRow | null)[]) => rows.filter(Boolean) as NavRow[];

    return [
      {
        key: 'intelligence',
        label: t('nav.groups.intelligence'),
        rows: compact([
          catRow('dashboard'),
          { key: 'daily-brief', to: '/daily-brief', label: t('nav.dailyBrief'), icon: Zap },
          catRow('market-insights'),
        ]),
      },
      {
        key: 'coverage',
        label: t('nav.groups.coverage'),
        rows: compact([
          catRow('mining-companies'),
          catRow('commodities'),
          catRow('infrastructure'),
          catRow('artisanal-mining'),
          catRow('financial-results'),
          catRow('financing'),
          catRow('regulatory'),
          catRow('government-figures'),
          { key: 'value-chain', to: '/value-chain', label: t('nav.valueChain'), icon: Workflow },
          catRow('consultancy-reports'),
          isAdmin ? catRow('internal-reports') : null,
        ]),
      },
      {
        key: 'regions',
        label: t('nav.groups.regions'),
        rows: [
          { key: 'CENTRAL', to: '/region/CENTRAL', label: t('nav.regionRows.CENTRAL'), icon: Map, recs: [{ code: 'CEMAC', label: 'CEMAC', intel: true }] },
          { key: 'WEST', to: '/region/WEST', label: t('nav.regionRows.WEST'), icon: Map, recs: [{ code: 'UEMOA', label: 'UEMOA', intel: true }, { code: 'ECOWAS', label: 'ECOWAS' }] },
          { key: 'EAST', to: '/region/EAST', label: t('nav.regionRows.EAST'), icon: Map, recs: [{ code: 'EAC', label: 'EAC', intel: true }] },
          { key: 'SOUTH', to: '/region/SOUTH', label: t('nav.regionRows.SOUTH'), icon: Map, recs: [{ code: 'SADC', label: 'SADC', intel: true }] },
          { key: 'NORTH', to: '/region/NORTH', label: t('nav.regionRows.NORTH'), icon: Map, recs: [{ code: 'AMU', label: 'AMU', intel: true }] },
        ],
      },
      {
        key: 'analysis',
        label: t('nav.groups.analysis'),
        rows: compact([
          { key: 'ground-truth', to: '/ground-truth', label: t('nav.groundTruth'), icon: Mountain },
          { key: 'weekly-reports', to: '/weekly-reports', label: t('nav.weeklyReports'), icon: FileDown },
          // Was admin-only while monthly_reports held no rows, so a paying
          // reader would have met "No monthly reports yet". The August 2026
          // edition is published, so the guard has outlived its reason: the
          // page already shows subscribers approved editions only.
          { key: 'africa-monthly', to: '/africa-monthly', label: t('nav.africaMonthly'), icon: Newspaper },
          // The StratMined Weekly newsletter editor stays internal: it is an
          // authoring tool, not subscriber content.
          isAdmin ? { key: 'stratmined-weekly', to: '/stratmined-weekly', label: t('nav.stratminedWeekly'), icon: Sparkles } : null,
          // Every signed-in reader sees the briefs tab. What they get inside
          // depends on the edition: the two open briefs in full, everything
          // published since as an extract with the request card under it. Hiding
          // the tab from non-Enterprise readers made that funnel unreachable by
          // exactly the people it was built for.
          { key: 'briefs', to: '/briefs', label: t('nav.strategicBriefs'), icon: FileText },
          { key: 'ri-cemac', to: '/regional-intel/CEMAC', label: t('nav.cemacIntel'), icon: Globe2 },
          { key: 'ri-uemoa', to: '/regional-intel/UEMOA', label: t('nav.uemoaIntel'), icon: Globe2 },
          { key: 'ri-eac', to: '/regional-intel/EAC', label: t('nav.eacIntel'), icon: Globe2 },
          { key: 'ri-sadc', to: '/regional-intel/SADC', label: t('nav.sadcIntel'), icon: Globe2 },
          { key: 'ri-amu', to: '/regional-intel/AMU', label: t('nav.amuIntel'), icon: Globe2 },
          isAdmin ? { key: 'baraza', to: '/baraza', label: t('nav.barazaForum'), icon: Globe2 } : null,
          isAdmin ? { key: 'cameroon', to: '/cameroon', label: t('nav.cameroonIntel'), icon: MapPin } : null,
        ]),
      },
      {
        key: 'workspace',
        label: t('nav.groups.workspace'),
        rows: compact([
          { key: 'alerts', to: '/alerts', label: t('nav.myAlerts'), icon: Bell },
          { key: 'bookmarks', to: '/bookmarks', label: t('nav.myBookmarks'), icon: Bookmark },
          { key: 'archive', to: '/archive', label: t('nav.archive'), icon: Archive },
          { key: 'subscription', to: '/subscription', label: t('nav.subscription'), icon: Crown },
          { key: 'support', to: '/support', label: t('nav.support'), icon: HelpCircle },
          isAdmin ? { key: 'system-health', to: '/system-health', label: t('nav.systemHealth'), icon: Activity } : null,
          isAdmin ? { key: 'settings', to: '/settings', label: t('nav.settings'), icon: Settings } : null,
        ]),
      },
    ];
  }, [t, isAdmin, canSeeBriefs, allowed]);

  const closeOnMobile = () => {
    if (window.innerWidth < 1024) setSidebarOpen(false);
  };

  const rowClass = (isActive: boolean) =>
    [
      'group flex items-center gap-[9px] rounded-md text-[11.5px] font-medium leading-[1.25]',
      'transition-colors duration-150',
      isSidebarCollapsed ? 'justify-center px-2 py-2' : 'px-[11px] py-1',
      isActive
        ? 'bg-[#C5922E] text-white shadow-[0_4px_12px_rgba(197,146,46,.22)]'
        : 'text-white/[.62] hover:bg-[#2C3A50] hover:text-white',
    ].join(' ');

  return (
    <>
      {/* Mobile menu button */}
      <button
        className="sm-print-hide fixed top-4 left-4 z-30 p-2 rounded-lg bg-[#1B2A4A] text-white lg:hidden shadow-lg"
        onClick={() => setSidebarOpen(!isSidebarOpen)}
        aria-label="Toggle menu"
      >
        <Menu size={20} />
      </button>

      <aside
        className={`sm-print-hide fixed top-0 left-0 z-30 h-full flex flex-col bg-[#1B2A4A] text-white
          border-r border-[#0E1730] transition-all duration-200
          ${isSidebarCollapsed ? 'w-20' : 'w-60'}
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      >
        {/* ---- Logo row (72px) ---- */}
        <div className="flex items-center justify-between h-[72px] flex-none px-[18px] border-b border-[#2C3A50]">
          <div className="flex items-center gap-[11px] min-w-0">
            <svg width="26" height="26" viewBox="0 0 64 64" fill="none" className="flex-none">
              <path d="M32 8L4 24L32 40L60 24L32 8Z" fill="#C5922E" fillOpacity="0.92" />
              <path d="M4 32L32 48L60 32" stroke="#C5922E" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M4 40L32 56L60 40" stroke="#C8963E" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.6" />
            </svg>
            {!isSidebarCollapsed && (
              <div className="min-w-0">
                <div
                  className="text-[17px] font-bold leading-none text-white truncate"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: '.004em' }}
                >
                  Strat<b className="text-[#C5922E] font-extrabold">Mined</b>
                </div>
                <div className="mt-[3px] text-[8.5px] font-semibold uppercase tracking-[.2em] text-[#C5922E]">
                  {t('nav.portalEyebrow')}
                </div>
              </div>
            )}
          </div>

          <button
            className="hidden lg:flex items-center justify-center w-7 h-7 rounded-md hover:bg-[#2C3A50] text-white/40 hover:text-white transition-colors"
            onClick={toggleSidebarCollapse}
            aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isSidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        {/* ---- Search ---- */}
        {!isSidebarCollapsed && (
          <div className="flex-none px-[14px] pt-[14px] pb-[10px]">
            <div className="flex items-center gap-2 h-[34px] px-[11px] rounded-lg bg-white/[.06] border border-white/[.08] focus-within:border-[#C5922E]/50 transition-colors">
              <Search size={13} className="flex-none text-white/45" strokeWidth={1.8} />
              <input
                ref={searchRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('nav.searchPlaceholder')}
                className="flex-1 min-w-0 bg-transparent text-[12px] text-white placeholder-white/40 outline-none"
              />
              <span
                className="flex-none text-[9px] text-white/30 border border-white/[.14] rounded-[3px] px-1 py-px"
                style={{ fontFamily: "'Space Mono', monospace" }}
              >
                &#8984;K
              </span>
            </div>
          </div>
        )}

        {/* ---- Grouped navigation ---- */}
        <nav
          className="flex-1 overflow-y-auto px-[10px] pt-0.5 pb-[10px]"
          style={{
            maskImage: 'linear-gradient(#000 97%, transparent)',
            WebkitMaskImage: 'linear-gradient(#000 97%, transparent)',
          }}
        >
          {groups.map((group) => (
            <div key={group.key}>
              {!isSidebarCollapsed ? (
                <div className="px-3 pt-2 pb-[3px] text-[9px] font-bold uppercase tracking-[.16em] text-white/[.32]">
                  {group.label}
                </div>
              ) : (
                <div className="my-2 mx-2 border-t border-[#2C3A50]" />
              )}

              <div className="space-y-px">
                {group.rows.map((row) => {
                  const Icon = row.icon;
                  const count = row.countSlug ? counts[row.countSlug] : undefined;
                  const isRecParent =
                    !!row.recs && location.pathname.startsWith(`/region/${row.key}`);

                  return (
                    <React.Fragment key={row.key}>
                      <NavLink
                        to={row.to}
                        onClick={closeOnMobile}
                        className={({ isActive }) => rowClass(isActive)}
                        title={isSidebarCollapsed ? row.label : undefined}
                      >
                        {({ isActive }) => (
                          <>
                            {isSidebarCollapsed ? (
                              <Icon size={17} className="flex-none" strokeWidth={1.5} />
                            ) : (
                              <span
                                className={`w-1 h-1 rounded-full flex-none ${
                                  isActive ? 'bg-white/90' : 'bg-white/20 group-hover:bg-white/40'
                                }`}
                              />
                            )}
                            {!isSidebarCollapsed && (
                              <>
                                <span className="flex-1 min-w-0 truncate">{row.label}</span>
                                {!!count && (
                                  <span
                                    className={`text-[10px] font-semibold ${isActive ? 'text-white/80' : 'text-white/35'}`}
                                    style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                                  >
                                    {count}
                                  </span>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </NavLink>

                      {/* RECs stay reachable: they unfold under their region. */}
                      {!isSidebarCollapsed && isRecParent && row.recs?.map((rec) => (
                        <div key={rec.code} className="flex items-center pl-[26px] pr-[11px] gap-1.5">
                          <NavLink
                            to={`/rec/${rec.code}`}
                            onClick={closeOnMobile}
                            className={({ isActive }) =>
                              `flex items-center py-1 rounded-md text-[10.5px] font-medium transition-colors duration-150
                               ${isActive ? 'text-[#C5922E]' : 'text-white/40 hover:text-white/80'}`
                            }
                          >
                            <span className="truncate">{rec.label}</span>
                          </NavLink>
                          {/* Blocs with a weekly note are reachable from here too,
                              not only from the Analysis group further down. */}
                          {rec.intel && (
                            <NavLink
                              to={`/regional-intel/${rec.code}`}
                              onClick={closeOnMobile}
                              className={({ isActive }) =>
                                `py-1 text-[9.5px] font-semibold uppercase tracking-[.08em] transition-colors duration-150
                                 ${isActive ? 'text-[#C5922E]' : 'text-white/25 hover:text-white/70'}`
                              }
                            >
                              {t('nav.intelChip')}
                            </NavLink>
                          )}
                        </div>
                      ))}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* ---- User card ---- */}
        <div className="flex-none border-t border-[#2C3A50] px-4 py-[13px]">
          {isSidebarCollapsed ? (
            <div className="flex flex-col items-center gap-2">
              <div className="w-[34px] h-[34px] rounded-full bg-[#2C3A50] flex items-center justify-center text-[12.5px] font-bold text-[#C5922E]">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <button
                onClick={logout}
                className="p-1.5 rounded-md hover:bg-[#2C3A50] text-white/40 hover:text-red-400 transition-colors"
                title={t('common.signOut')}
              >
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-[11px]">
              <div className="w-[34px] h-[34px] flex-none rounded-full bg-[#2C3A50] flex items-center justify-center text-[12.5px] font-bold text-[#C5922E]">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-semibold text-white leading-[1.2] truncate">
                  {user?.name || 'User'}
                </div>
                <span
                  className="inline-block mt-1 px-1.5 py-0.5 rounded-[3px] text-[8.5px] font-bold uppercase tracking-[.14em] bg-[#C5922E]/[.16] text-[#C5922E]"
                  style={{ fontFamily: "'Space Mono', monospace" }}
                >
                  {user?.role || 'external'}
                </span>
              </div>
              <button
                onClick={logout}
                className="flex-none p-1.5 rounded-md hover:bg-[#2C3A50] text-white/40 hover:text-red-400 transition-colors"
                title={t('common.signOut')}
              >
                <LogOut size={16} />
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};
